import { FormStatus, P, Space, Timeline } from '@dnb/eufemia';
import { hourglass as HourglassIcon, stop as StopIcon } from '@dnb/eufemia/icons';
import { Decision } from '@portals/shared/portal/AppApiEntitlementDto';
import type { JSX } from 'react';

import DigitalIntegrationRequestDetails from '../DigitalIntegrationRequestDetails';

import style from './index.module.css';

export interface RequestProps {
  scopes: string[];
  testScopes: string[];
  externalTicketId: string | null;
  createdAt: string;
  decision: Decision;
  decisionMadeAt: string | null;
  decisionComment: string | null;
  externalTicketComment: string | null;
}

type RequestTimelineProps = {
  request: RequestProps;
  providerView?: boolean;
};

const texts = {
  accessTest: 'Access to the test environment',
  accessProduction: 'Access to the live environment',
  accessTestProd: 'Access to the test and live environments',
  reviewed: 'Request has been sent for review',
};

function getAccessText(request: RequestProps): string {
  const hasTest = request.testScopes.length > 0;
  const hasLive = request.scopes.length > 0;
  if (hasTest && hasLive) return texts.accessTestProd;
  if (hasTest) return texts.accessTest;
  return texts.accessProduction;
}

function PreSubmit({ request, providerView }: RequestTimelineProps): JSX.Element {
  return (
    <>
      <Timeline.Item
        icon={<HourglassIcon />}
        infoMessage={providerView ? 'The request is being processed.' : 'Your request is being processed.'}
        state="current"
        title="Request is being processed"
      />
      <Timeline.Item state="upcoming" title={texts.reviewed} />
      <Timeline.Item state="upcoming" title={getAccessText(request)} />
    </>
  );
}

function Pending({ request, providerView }: RequestTimelineProps): JSX.Element {
  return (
    <>
      {request.externalTicketId && (
        <Timeline.Item icon={<HourglassIcon />} state="completed" title="Request has been processed in Service Now" />
      )}
      <Timeline.Item
        infoMessage={
          <>
            <P bottom="small" size="small">
              {providerView ? 'The request has been sent for review.' : 'Your request has been sent for review.'}
            </P>
            {request.externalTicketId && <DigitalIntegrationRequestDetails requestId={request.externalTicketId} />}
          </>
        }
        state="current"
        title="Request has been sent for review"
      />
      <Timeline.Item state="upcoming" title={getAccessText(request)} />
    </>
  );
}

function Level1Approved({ request }: RequestTimelineProps) {
  return (
    <>
      <Timeline.Item state="completed" title="Request has been processed in Service Now" />
      <Timeline.Item
        infoMessage={
          request.externalTicketId && <DigitalIntegrationRequestDetails requestId={request.externalTicketId} />
        }
        state="completed"
        title="Request has been approved by API provider"
      />
      <Timeline.Item icon={HourglassIcon} state="current" title="The Portals team are finalizing the request" />
      <Timeline.Item state="upcoming" title={getAccessText(request)} />
    </>
  );
}

function Denied({ request }: RequestTimelineProps) {
  const toLocalDateString = (date: string) => new Date(date).toLocaleString();

  return (
    <>
      <Timeline.Item
        state="completed"
        subtitle={request.decisionMadeAt ? toLocalDateString(request.decisionMadeAt) : ''}
        title="Request reviewed"
      />

      <Timeline.Item icon={<StopIcon />} state="current" title={getAccessText(request)} />
    </>
  );
}

export default function RequestTimeline({ request, providerView }: RequestTimelineProps): JSX.Element {
  const toLocalDateString = (date: string) => new Date(date).toLocaleString();

  function timelineComponent(decision: Decision) {
    switch (decision) {
      case Decision.PRESUBMIT: {
        return <PreSubmit providerView={providerView} request={request} />;
      }
      case Decision.PENDING: {
        return <Pending providerView={providerView} request={request} />;
      }
      case Decision.LEVEL1_APPROVED: {
        return <Level1Approved request={request} />;
      }
      case Decision.DENIED: {
        return <Denied request={request} />;
      }
      default: {
        return <Pending providerView={providerView} request={request} />;
      }
    }
  }

  return (
    <>
      <Timeline bottom="small" top="small">
        <Timeline.Item state="completed" subtitle={toLocalDateString(request.createdAt)} title="Access request sent" />
        {timelineComponent(request.decision)}
      </Timeline>

      {request.decision === Decision.DENIED && (
        <Space bottom="medium" className={style['FormStatus']} left="x-large">
          <FormStatus
            text={
              <P>
                Request for live access was rejected with the following reason:{' '}
                <i>{request.externalTicketComment || request.decisionComment}</i>
                {request.externalTicketId && <DigitalIntegrationRequestDetails requestId={request.externalTicketId} />}
              </P>
            }
          />
        </Space>
      )}
    </>
  );
}
